import { convertUTCToDateBarFormat } from 'utils/utils';
import {
	BROWSER_TZ,
	PAID_COLUMN_MIN_WIDTH_HEADERS,
	REGISTRATION_COLUMN_MIN_WIDTH_HEADERS,
	RESPONSES_INDEX,
} from './constants';
import { getData } from 'services/api';

export const formatDate = (orderDate: string) => {
	const { date, time } = convertUTCToDateBarFormat(orderDate, BROWSER_TZ);
	return `${date} | ${time}`;
};

/**
 * Venue-facing copy for backend status strings. The backend tags the switched-away
 * row `'Switched To'` and the switched-onto row `'Switched From'`; managers see them
 * as "Switch Origin" / "Switch Destination". Any status not listed renders verbatim.
 */
const STATUS_LABELS: Record<string, string> = {
	'Switched To': 'Switch Origin',
	'Switched From': 'Switch Destination',
};

export const formatStatusLabel = (status: string): string => STATUS_LABELS[status] ?? status;

// Column width at index `responsesIdx` will be used for custom fields responses
export const getCellIndex = (index: number, headings: string[], responsesIdx: number, customHeadings: string[]) => {
	// If the table has no responses or index is less than `responsesIdx`, return same index
	if (headings.length === customHeadings.length || index <= responsesIdx) return index;
	// Last three columns are amount, date and ticket type
	if (index >= headings.length - 3) return index - (headings.length - customHeadings.length - 1);
	// For responses use always index `responsesIdx`
	return responsesIdx;
};

/**
 * Builds the CSS grid-template-columns value for the attendee table rows.
 *
 * Uses `minmax(minPx, 1fr)` per column instead of plain `1fr` so that each
 * column enforces its minimum width. When the sum of minimums exceeds the
 * viewport the grid row overflows Table.TableContainer, which then displays
 * a horizontal scrollbar via its `overflow-x: auto`.
 */
export const buildGridColumns = (
	headings: string[],
	customHeadings: string[],
	eventMode = 'normal',
	actionWidth = 40
): string => {
	const widths = eventMode === 'normal' ? PAID_COLUMN_MIN_WIDTH_HEADERS : REGISTRATION_COLUMN_MIN_WIDTH_HEADERS;
	const columns = headings.map((_, i) => {
		const index = getCellIndex(i, headings, RESPONSES_INDEX, customHeadings);
		const minW = widths[index] ?? 120;
		return `minmax(${minW}px, 1fr)`;
	});
	columns.push(`${actionWidth}px`); // content-sized actions column
	return columns.join(' ');
};

export const downloadTicketQR = async (id: number, email: string) => {
	const response = await getData(`/tickets/${id}/qr`, {
		headers: { 'Content-Type': 'image/png' },
		responseType: 'blob',
	});

	const url = window.URL.createObjectURL(response);
	const a = document.createElement('a');
	a.href = url;
	a.download = (email || 'ticket') + '.png';
	document.body.appendChild(a);
	a.click();
	window.URL.revokeObjectURL(url);
	a.remove();
};
